import React, { useState } from 'react';
import { LucideIcon, ChevronUp, ChevronDown, Users } from 'lucide-react';
import { Booking } from '../types/dashboard';
import BookingDetailsModal from './BookingDetailsModal';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  color: string;
  bookings?: Booking[];
}

export default function StatCard({ title, value, icon: Icon, color, bookings = [] }: StatCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
      </div>

      {bookings.length > 0 && (
        <>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 mt-2"
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {isExpanded ? "Voir moins" : `Voir les ${bookings.length} réservations`}
          </button>

          {isExpanded && (
            <div className="mt-4 space-y-3 max-h-64 overflow-y-auto">
              {bookings.map((booking, index) => (
                <div
                  key={index}
                  className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                  onClick={() => setSelectedBooking(booking)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {booking.property.name}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {booking.guest_first_name} {booking.guest_last_name}
                      </p>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center">
                          <Users size={14} className="mr-1" />
                          {booking.guest_count}
                        </span>
                        <div className="flex gap-1">
                          {booking.cleaning_service && (
                            <span className="bg-white/20 px-1 rounded">🧹</span>
                          )}
                          {booking.bed_sheets_service && (
                            <span className="bg-white/20 px-1 rounded">🛏️</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedBooking && (
            <BookingDetailsModal
              booking={selectedBooking}
              onClose={() => setSelectedBooking(null)}
            />
          )}
        </>
      )}
    </div>
  );
}